<template>
    <div>
        <loading v-if="fetchingFilters" class="mb-32"></loading>

        <div v-else-if="getFilters.length > 0 || can.filters">
            <h2 class="text-heading-1 font-semibold first-letter:uppercase mb-8">
                {{ $t('jobs.create.headers.tags') }}
            </h2>

            <p class="w-1/2 text-primary-80 mb-16">
                {{ $t('jobs.filters.description') }}
            </p>

            <div class="flex flex-wrap items-center mb-16 -mr-16">
                <div v-for="filter in getFilters"
                    :key="'filter-' + filter.id"
                    class="w-1/3 mb-8"
                >
                    <div class="mr-8 relative">
                        <checkbox v-model="filters"
                                  :value="filter.id"
                                  class="border border-primary-20 bg-white rounded pl-8 pr-64"
                        >
                            <div class="pt-8 mb-8">
                                {{ filter.title[locale] }}
                            </div>
                        </checkbox>
                        <div v-if="can.filters" class="absolute inset-y-0 right-0 flex items-center pointer-events-none space-x-8 mr-8">
                            <tooltip v-if="noTitleForLang(filter.title)"
                                     offset="0,16"
                                     wrapper-class="inline-flex pointer-events-auto">
                                <template #default>
                                    <svg-icon class="text-semantic-warning -mt-2 w-16 h-16 ml-8" type="warning"></svg-icon>
                                </template>
                                <template #tooltip>
                                    <div class="whitespace-nowrap">
                                        <p>{{ $t('general.missing_translation') }}</p>
                                    </div>
                                </template>
                            </tooltip>
                            <button class="text-primary-80 trans-ease hover:text-primary pointer-events-auto" @click="editFilter(filter)">
                                <svg-icon class="max-w-16 max-h-16" type="edit"></svg-icon>
                            </button>
                            <button class="text-primary-80 trans-ease hover:text-semantic-error pointer-events-auto" @click="confirmDeleteFilter(filter)">
                                <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="can.filters" class="w-1/3 mb-8 flex">
                    <simple-button class="flex-grow mr-8" icon="add-circle" @click="createFilter">{{ $t('jobs.filters.add') }}</simple-button>
                </div>
            </div>
        </div>

        <popup-modal-confirm
            v-if="deletingFilter"
            :confirmText="$t('general.delete_subject', {subject: $t('jobs.filters.single')})"
            :message="$t('general.confirm_delete_subject', {subject: $t('jobs.filters.single')})"
            :working="performingDeleteFilterRequest"
            @close="cancelDeleteFilter"
            @confirm="deleteFilter(deletingFilter)"
        ></popup-modal-confirm>

        <filter-editor
            v-if="editingFilter"
            :filter="editingFilter"
            @close="stopEdit()">
        </filter-editor>

        <filter-editor v-if="creatingFilter" @close="stopCreate()">
        </filter-editor>
    </div>
</template>

<script>
import * as jobs from '../../services/JobService';
import Filter from '../../models/Filter';
import Loading from './Loading.vue';
import Checkbox from '../forms/Checkbox.vue';
import SvgIcon from './SvgIcon.vue';
import SimpleButton from './SimpleButton.vue';
import PopupModalConfirm from './PopupModalConfirm';
import FilterEditor from './FilterEditor';
import {normalizeJson} from '../../../shared/helpers';
import Tooltip from '../navigation/Tooltip';
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";
import {useRepo} from "pinia-orm";

export default {
    name: 'JobFilters',
    components: {Tooltip, FilterEditor, PopupModalConfirm, SimpleButton, SvgIcon, Checkbox, Loading},
    props: {
        job: Object,
        value: Array,
    },
    data: function () {
        return {
            creatingFilter: false,
            editingFilter: null,
            deletingFilter: null,
            performingDeleteFilterRequest: false,
            fetchingFilters: false,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        locale() {
            return this.mainStore.locale;
        },
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        can() {
            return {
                filters: this.$can(['jobs.create', 'jobs.update']),
            };
        },
        filters: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
        getFilters() {
            return useRepo(Filter).all();
        },
    },
    methods: {
        fetchFilters() {
            this.fetchingFilters = true;

            jobs.filters().then(result => {
                Filter.insert({data: normalizeJson(result.data)});
            }).finally(() => this.fetchingFilters = false);
        },
        confirmDeleteFilter(filter) {
            this.deletingFilter = filter;
        },
        cancelDeleteFilter() {
            this.deletingFilter = null;
        },
        deleteFilter(filter) {
            this.performingDeleteFilterRequest = true;

            jobs.destroyFilter(filter.id).then(() => {
                return Filter.delete(filter.id);
            }).then(() => {
                this.deletingFilter = null;
                this.performingDeleteFilterRequest = false;
            });
        },
        createFilter() {
            this.creatingFilter = true;
        },
        stopCreate() {
            this.creatingFilter = false;
        },
        editFilter(filter) {
            this.editingFilter = filter;
        },
        stopEdit() {
            this.editingFilter = null;
        },
        noTitleForLang(title) {
            //Check for each language if the title is filled in, otherwise there is no point in publishing for this lang
            return Object.entries(title)
                .filter(([key, value]) => {
                    if (value === '' && this.currentCompany.jobsite.active_languages.includes(value)) {
                        return value;
                    }
                })
                .map(lang => lang[0]).length;
        },
    },
    mounted() {
        if (this.job) {
            this.filters = this.job.filters.map(filter => filter.id);
        }

        this.fetchFilters();
    },
};
</script>
